/**
 * @fileoverview Link Indicator Overlay
 * 
 * Shows a visual indicator (link icon) on shapes that have linked diagrams.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import React from 'react';
import { Group, Circle, Path } from 'react-konva';
import Konva from 'konva';
import { asTouchKonvaHandler } from '../../../lib/konvaEventAdapters';

interface LinkIndicatorProps {
  x: number;
  y: number;
  onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}

/**
 * Link icon indicator overlay
 * Positioned at top-right corner of shape
 */
export const LinkIndicator: React.FC<LinkIndicatorProps> = ({ x, y, onClick }) => {
  const radius = 10;
  // The path below is centered around (12, 8) in its own coordinate space:
  // - two circles with centers at (8,8) and (16,8) and radius 4 => bbox [4..20] x [4..12]
  // Using the true center avoids the icon looking "off" inside the circle.
  const iconScale = 0.6;
  const iconCenter = { x: 12, y: 8 };

  return (
    <Group
      x={x}
      y={y}
      name="link-indicator"
      listening={true}
      perfectDrawEnabled={false}
      onClick={onClick}
      onTap={asTouchKonvaHandler(onClick)}
      onMouseEnter={() => {
        document.body.style.cursor = 'pointer';
      }}
      onMouseLeave={() => {
        document.body.style.cursor = 'default';
      }}
    >
      {/* Background circle */}
      <Circle
        x={0}
        y={0}
        radius={radius}
        fill="#3b82f6"
        stroke="#ffffff"
        strokeWidth={1.5}
        listening={true}
        perfectDrawEnabled={false}
        shadowBlur={4}
        shadowColor="rgba(0, 0, 0, 0.2)"
        shadowOffsetY={1}
      />
      {/* Link icon (simplified chain link) */}
      <Group
        x={-iconCenter.x * iconScale}
        y={-iconCenter.y * iconScale}
        scaleX={iconScale}
        scaleY={iconScale}
        listening={false}
        perfectDrawEnabled={false}
      >
        {/* Two overlapping circles to form a chain link */}
        <Path
          data="M 8 4 A 4 4 0 0 1 8 12 A 4 4 0 0 1 8 4 M 16 4 A 4 4 0 0 1 16 12 A 4 4 0 0 1 16 4"
          stroke="#ffffff"
          strokeWidth={2}
          fill="none"
          listening={false}
          perfectDrawEnabled={false}
          lineCap="round"
          lineJoin="round"
        />
      </Group>
    </Group>
  );
};
